import React from 'react';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { testimonials, googleBusiness } from '../mockData';

const Reviews = () => {
  const { rating, reviewCount, profileUrl } = googleBusiness;

  return (
    <section id="reviews" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
              Recensioni Verificate
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Cosa Dicono di Noi
          </h2>

          {/* Google Rating Badge */}
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-rating-badge"
            className="inline-flex items-center gap-4 bg-white border-2 border-gray-200 hover:border-green-500 rounded-2xl px-6 py-4 shadow-md hover:shadow-xl transition-all group"
          >
            {/* Google G */}
            <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>

            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-slate-900">{rating}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={18}
                      className={s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                Basato su <strong>{reviewCount}+ recensioni</strong> Google
              </div>
            </div>

            <ExternalLink size={20} className="text-gray-400 group-hover:text-green-600 transition-colors" />
          </a>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {testimonials.map((t) => (
            <div
              key={t.id}
              data-testid={`testimonial-${t.id}`}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 relative"
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 left-8 bg-green-600 rounded-full p-2 shadow-lg">
                <Quote size={20} className="text-white" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-4 mt-2">
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>

              {/* Text */}
              <p className="text-gray-700 leading-relaxed mb-6 italic">
                "{t.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <div className="bg-gradient-to-br from-green-500 to-green-700 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {t.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm text-gray-500">{t.role}</div>
                  {t.when && (
                    <div className="text-xs text-gray-400 mt-0.5">{t.when}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Bottom */}
        <div className="text-center mt-12">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="read-all-reviews-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-lg hover:bg-green-600 transition-all hover:shadow-xl font-semibold group"
          >
            Leggi tutte le recensioni su Google
            <ExternalLink size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
